import { useCallback, useEffect, useState } from "react";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import { useHubStore } from "@/components/hub/use-hub-store";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useUpdateMagicMomentState } from "@/lib/api/mentorships";
import { Day1Banner } from "./day1-banner";
import { Day2Banner } from "./day2-banner";
import { Day2Modal } from "./day2-modal";
import { ReactivationBanner } from "./reactivation-banner";
import { MomentumAlertBanner } from "./momentum-alert-banner";
import { useMagicMomentStore } from "./use-magic-moment-store";
import { HotTakesDialogHost } from "@/components/conversations/activities/hot-takes";
import { useMentorMomentBannerState } from "./use-mentor-moment-banner-state";

interface MentorMomentBannerProps {
  currentConversation: string;
}

export function MentorMomentBanner({
  currentConversation,
}: MentorMomentBannerProps) {
  const { mentorship } = useGetChannelMentorship();
  const {
    dayState,
    day1Enabled,
    day2Enabled,
    modalDismissed,
    bannerDismissed,
    isBannerExpired,
    showMomentumAlertBanner,
    showReactivationBanner,
    teenGender,
    teenId,
    teenName,
  } = useMentorMomentBannerState({ mentorship });

  const { track } = useTrackEvent();
  const { mutate: updateMagicMomentState } = useUpdateMagicMomentState();
  const resetView = useMagicMomentStore((s) => s.resetView);
  const isModalDismissed = useMagicMomentStore((s) => s.isModalDismissed);
  const openHub = useHubStore((s) => s.open);
  const navigateToHubView = useHubStore((s) => s.navigateToView);
  const setMessageInput = useMessageInputStore((s) => s.setMessageInput);

  const [showModal, setShowModal] = useState(false);
  const [hotTakesOpen, setHotTakesOpen] = useState(false);

  const mentorshipId = mentorship?.id || null;

  useEffect(() => {
    resetView();
  }, [currentConversation, resetView]);

  useEffect(() => {
    if (
      dayState === "day2" &&
      day2Enabled &&
      !modalDismissed &&
      currentConversation &&
      !isModalDismissed(mentorshipId ?? "")
    ) {
      setShowModal(true);
    }
  }, [
    dayState,
    day2Enabled,
    modalDismissed,
    currentConversation,
    isModalDismissed,
    mentorshipId,
  ]);

  const dismissBanner = useCallback(
    (reason: "x_button" | "action_completed" | "message_sent") => {
      if (!mentorshipId || !dayState) return;
      const eventName =
        dayState === "day1"
          ? PostHogEvents.DAY1_BANNER_DISMISS
          : PostHogEvents.DAY2_BANNER_DISMISS;
      track(eventName, { teen_id: teenId, dismiss_reason: reason });
      updateMagicMomentState({
        mentorshipId,
        fields:
          dayState === "day1"
            ? { day1_banner_dismissed: true }
            : { day2_banner_dismissed: true },
      });
    },
    [mentorshipId, dayState, teenId, track, updateMagicMomentState],
  );

  const handleShareMoment = useCallback(() => {
    dismissBanner("action_completed");
    openHub();
    navigateToHubView("create-moment");
  }, [dismissBanner, openHub, navigateToHubView]);

  const handleStartSwap = useCallback(() => {
    dismissBanner("action_completed");
    openHub();
    navigateToHubView("create-swap");
  }, [dismissBanner, openHub, navigateToHubView]);

  const handleMomentumStartSwap = useCallback(() => {
    openHub();
    navigateToHubView("create-swap");
  }, [openHub, navigateToHubView]);

  const handleInsertToChat = useCallback(
    (text: string) => {
      setMessageInput(currentConversation, text);
      // Fire banner_dismiss with reason=message_sent to track intent.
      // Note: we don't actually wait for the send — the mentor may still edit.
      // The banner stays visible so they can re-reference the template.
      if (mentorshipId && dayState === "day2") {
        track(PostHogEvents.DAY2_BANNER_DISMISS, {
          teen_id: teenId,
          dismiss_reason: "message_sent",
        });
      }
    },
    [
      currentConversation,
      setMessageInput,
      mentorshipId,
      dayState,
      teenId,
      track,
    ],
  );

  // Reactivation takes priority over any momentum/day1/day2 window.
  if (showReactivationBanner) {
    return (
      <div className="mx-4">
        <ReactivationBanner
          teenName={teenName}
          teenId={teenId}
          conversationId={currentConversation}
        />
      </div>
    );
  }

  if (showMomentumAlertBanner && mentorshipId) {
    return (
      <div className="mx-4">
        <MomentumAlertBanner
          teenName={teenName}
          teenGender={teenGender}
          teenId={teenId}
          conversationId={currentConversation}
          onStartHotTakes={() => setHotTakesOpen(true)}
          onStartMagicMoment={handleInsertToChat}
          onStartSwap={handleMomentumStartSwap}
        />
        <HotTakesDialogHost
          open={hotTakesOpen}
          mentorshipId={mentorshipId}
          teenName={teenName || "Teen"}
          onOpenChange={setHotTakesOpen}
        />
      </div>
    );
  }

  if (!dayState || bannerDismissed) {
    return null;
  }

  if (dayState === "day1" && day1Enabled) {
    return (
      <div className="mx-4">
        <Day1Banner teenId={teenId} conversationId={currentConversation} />
      </div>
    );
  }

  if (dayState === "day2" && day2Enabled && !isBannerExpired) {
    return (
      <div className="mx-4">
        {showModal && mentorshipId && (
          <Day2Modal
            mentorshipId={mentorshipId}
            teenId={teenId}
            conversationId={currentConversation}
            open={showModal}
            onDismiss={() => setShowModal(false)}
          />
        )}
        <Day2Banner
          teenGender={teenGender}
          teenId={teenId}
          conversationId={currentConversation}
          onShareMoment={handleShareMoment}
          onStartSwap={handleStartSwap}
          onInsertToChat={handleInsertToChat}
        />
      </div>
    );
  }

  return null;
}
